import React, { useEffect, useState } from "react";
import { api, formatApiError, formatRupiah } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

export default function Budget() {
  const [items, setItems] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [insight, setInsight] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);

  const load = async () => {
    const [b, c] = await Promise.all([
      api.get("/budgets"), api.get(`/laporan/budget-vs-aktual?period=${period}`),
    ]);
    setItems(b.data); setComparison(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budgets", { ...form, planned: Number(form.planned), period });
      toast.success("Budget tersimpan");
      setForm({}); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const runAI = async () => {
    if (!comparison?.items?.length) return toast.error("Belum ada data untuk dianalisis");
    setLoadingAI(true); setInsight("");
    try {
      const ctx = `Periode: ${period}\n` + comparison.items.map((i) =>
        `- ${i.category}: Plan ${i.planned}, Actual ${i.actual}, Variance ${i.variance} (${i.variance_pct.toFixed(1)}%)`
      ).join("\n");
      const { data } = await api.post("/ai/insight", {
        context: ctx,
        question: "Analisis performa budget vs aktual bulan ini. Berikan insight dan rekomendasi actionable.",
      });
      setInsight(data.insight);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoadingAI(false); }
  };

  return (
    <div>
      <PageHeader
        title="Budget vs Aktual"
        subtitle="Perencanaan anggaran vs realisasi + AI insight"
        testId="budget-header"
        actions={
          <>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="budget-period" className="w-40 rounded-none bg-neutral-900 border-neutral-800 h-11" />
            <Button onClick={runAI} disabled={loadingAI} data-testid="budget-ai-btn" className="rounded-none bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold">
              <Sparkles size={16} className="mr-1" /> {loadingAI ? "MEMPROSES..." : "AI INSIGHT"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="budget-add-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold"><Plus size={16} className="mr-1" /> Tambah Budget</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                <DialogHeader><DialogTitle className="font-display font-black text-2xl">Tambah Budget</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Kategori</Label>
                    <Input required value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" placeholder="BBM / Gaji / Sparepart" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Planned (Rp)</Label>
                    <Input required type="number" value={form.planned || ""} onChange={(e) => setForm({ ...form, planned: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                  </div>
                  <div className="text-xs text-neutral-500 font-mono">Periode: {period}</div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-800">Batal</Button>
                    <Button type="submit" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">Simpan</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      <Section>
        {comparison?.items?.length > 0 && (
          <div className="border border-neutral-800 bg-[#141414] p-5 mb-4">
            <div className="font-mono text-[10px] uppercase text-neutral-500 mb-3">// COMPARISON — {period}</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={comparison.items}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="category" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626" }} />
                <Legend />
                <Bar dataKey="planned" fill="#FFD700" name="Planned" />
                <Bar dataKey="actual" fill="#FF6600" name="Actual" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {insight && (
          <div className="border border-[#FFD700] bg-[#FFD700]/5 p-5 mb-4" data-testid="ai-insight">
            <div className="font-mono text-[10px] uppercase text-[#FFD700] mb-2 flex items-center gap-1"><Sparkles size={12} /> AI INSIGHT</div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{insight}</div>
          </div>
        )}

        {comparison?.items?.length > 0 && (
          <div className="border border-neutral-800 bg-[#141414] mb-4">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Kategori</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Planned</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Actual</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Variance</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparison.items.map((i, idx) => (
                  <TableRow key={idx} className="border-neutral-800">
                    <TableCell className="font-mono">{i.category}</TableCell>
                    <TableCell className="font-mono">{formatRupiah(i.planned)}</TableCell>
                    <TableCell className="font-mono">{formatRupiah(i.actual)}</TableCell>
                    <TableCell className={`font-mono font-bold ${i.variance >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatRupiah(i.variance)}</TableCell>
                    <TableCell className={`font-mono ${i.variance >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{i.variance_pct.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">// SEMUA BUDGET</div>
        {items.length === 0 ? <Empty /> : (
          <div className="border border-neutral-800 bg-[#141414]">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Periode</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Kategori</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Planned</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((b) => (
                  <TableRow key={b.id} className="border-neutral-800">
                    <TableCell className="font-mono">{b.period}</TableCell>
                    <TableCell className="font-mono">{b.category}</TableCell>
                    <TableCell className="font-mono">{formatRupiah(b.planned)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
